import { UNIT_SYSTEM } from "constants/units.contants";
import { InputNumber } from "models/packingList";
import { Unit } from "models/units";

const MM_PER_INCH = 25.4;
const KG_PER_LB = 0.453592;
const DISPLAY_PRECISION = 2;

const LENGTH_PER_UNIT: Record<string, number> = {
  mm: 1,
  cm: 10,
  m: 1000,
  inch: MM_PER_INCH,
  ft: 304.8,
  fts: 304.8,
  [UNIT_SYSTEM.METRIC]: 1,
  [UNIT_SYSTEM.IMPERIAL]: MM_PER_INCH,
};

const KG_PER_UNIT: Record<string, number> = {
  kg: 1,
  lb: KG_PER_LB,
  [UNIT_SYSTEM.METRIC]: 1,
  [UNIT_SYSTEM.IMPERIAL]: KG_PER_LB,
};

const roundToPrecision = (value: number) => Math.round(value * 10 ** DISPLAY_PRECISION) / 10 ** DISPLAY_PRECISION;

export const toDisplayLength = (mm: InputNumber, unit: Unit | string): InputNumber =>
  mm == null ? mm : roundToPrecision(mm / (LENGTH_PER_UNIT[unit] ?? 1));

export const fromDisplayLength = (value: InputNumber, unit: Unit | string): InputNumber =>
  value == null ? value : value * (LENGTH_PER_UNIT[unit] ?? 1);

export const toDisplayWeight = (kg: InputNumber, unit: Unit | string): InputNumber =>
  kg == null ? kg : roundToPrecision(kg / (KG_PER_UNIT[unit] ?? 1));

export const fromDisplayWeight = (value: InputNumber, unit: Unit | string): InputNumber =>
  value == null ? value : value * (KG_PER_UNIT[unit] ?? 1);

const convertLengthValue = (value: unknown, unit: string) => {
  if (value == null || value === "") return value;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  return numeric / (LENGTH_PER_UNIT[unit] ?? 1);
};

const convertWeightValue = (value: unknown, unit: string) => {
  if (value == null || value === "") return value;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  return numeric / (KG_PER_UNIT[unit] ?? 1);
};

export const convertLengthBetweenUnits = (value: unknown, fromUnit: string, toUnit: string) => {
  if (value == null || value === "") return value;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  const mm = numeric * (LENGTH_PER_UNIT[fromUnit] ?? 1);
  return mm / (LENGTH_PER_UNIT[toUnit] ?? 1);
};

export const convertWeightBetweenUnits = (value: unknown, fromUnit: string, toUnit: string) => {
  if (value == null || value === "") return value;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  const kg = numeric * (KG_PER_UNIT[fromUnit] ?? 1);
  return kg / (KG_PER_UNIT[toUnit] ?? 1);
};

const convertSpaceList = <T extends { spaces?: Array<Record<string, unknown>> }>(
  items: T[],
  fromLength: string,
  toLength: string,
  fromWeight: string,
  toWeight: string
): T[] =>
  items.map((item) => ({
    ...item,
    spaces: item.spaces?.map((space) => ({
      ...space,
      length: convertLengthBetweenUnits(space.length, fromLength, toLength),
      width: convertLengthBetweenUnits(space.width, fromLength, toLength),
      height: convertLengthBetweenUnits(space.height, fromLength, toLength),
      maxWeight: convertWeightBetweenUnits(space.maxWeight, fromWeight, toWeight),
    })),
  }));

export const convertTransportToUnits = convertSpaceList;

const ROUND_TYPES = new Set(["roll", "barrels", "pipes"]);

export const applyOptionsUnitsToRequest = <
  T extends { options?: { lengthUnits?: string; weightUnits?: string; rollPlacement?: number }; groups?: unknown[] },
>(
  body: T
): T => {
  const lengthUnits = body.options?.lengthUnits ?? "mm";
  const weightUnits = body.options?.weightUnits ?? "kg";
  const rollPlacement = body.options?.rollPlacement ?? 4;
  const next = structuredClone(body);

  if (next.options) {
    next.options.lengthUnits = lengthUnits;
    next.options.weightUnits = weightUnits;
    next.options.rollPlacement = rollPlacement;
  }

  if (!Array.isArray(next.groups)) return next;

  next.groups = next.groups.map((group) => {
    if (!group || typeof group !== "object") return group;
    const nextGroup = group as {
      items?: Array<{
        type?: string;
        weight?: unknown;
        size?: Record<string, unknown>;
        stacking?: Record<string, unknown>;
      }>;
      pallets?:
        | false
        | {
            depth?: unknown;
            maxWeight?: unknown;
            weight?: unknown;
            size?: Record<string, unknown>;
            stacking?: Record<string, unknown>;
          };
    };

    nextGroup.items = nextGroup.items?.map((item) => {
      const size = item.size
        ? {
            ...item.size,
            length: convertLengthValue(item.size.length, lengthUnits),
            width: convertLengthValue(item.size.width, lengthUnits),
            height: convertLengthValue(item.size.height, lengthUnits),
            radius: convertLengthValue(item.size.radius, lengthUnits),
            diameter: convertLengthValue(item.size.diameter, lengthUnits),
          }
        : item.size;
      const stacking = item.stacking
        ? {
            ...item.stacking,
            height: convertLengthValue(item.stacking.height, lengthUnits),
            topWeight: convertWeightValue(item.stacking.topWeight, weightUnits),
            ...(ROUND_TYPES.has(String(item.type)) ? { rollPlacement } : {}),
          }
        : item.stacking;

      return {
        ...item,
        weight: convertWeightValue(item.weight, weightUnits),
        size,
        stacking,
      };
    });

    if (nextGroup.pallets && typeof nextGroup.pallets === "object") {
      const pallet = nextGroup.pallets;
      nextGroup.pallets = {
        ...pallet,
        depth: convertLengthValue(pallet.depth, lengthUnits),
        maxWeight: convertWeightValue(pallet.maxWeight, weightUnits),
        weight: convertWeightValue(pallet.weight, weightUnits),
        size: pallet.size
          ? {
              ...pallet.size,
              length: convertLengthValue(pallet.size.length, lengthUnits),
              width: convertLengthValue(pallet.size.width, lengthUnits),
              height: convertLengthValue(pallet.size.height, lengthUnits),
            }
          : pallet.size,
        stacking: pallet.stacking
          ? {
              ...pallet.stacking,
              maxHeight: convertLengthValue(pallet.stacking.maxHeight, lengthUnits),
            }
          : pallet.stacking,
      };
    }

    return nextGroup;
  });

  return next;
};
